import React, { useState, useEffect } from 'react';
import { Dialog, Box, Typography, Button, TextField, Select, MenuItem, Divider } from '@mui/material';

interface DiscountModalProps {
  open: boolean;
  onClose: () => void;
  onApplyDiscount: (type: 'percent' | 'fixed', value: number) => void;
  subtotal: number;
}

const DiscountModal: React.FC<DiscountModalProps> = ({ open, onClose, onApplyDiscount, subtotal }) => {
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent');
  const [inputValue, setInputValue] = useState('0');
  const [isFocused, setIsFocused] = useState(false);

  // Handle focus to clear initial zero
  const handleFocus = () => {
    setIsFocused(true);
    if (inputValue === '0') {
      setInputValue('');
    }
  };

  // Handle blur to restore zero if empty
  const handleBlur = () => {
    setIsFocused(false);
    if (inputValue === '') {
      setInputValue('0');
    }
  };

  const handleApply = () => {
    const numericValue = parseFloat(inputValue);
    
    if (isNaN(numericValue)) {
      alert('Please enter a valid number');
      return;
    }

    if (numericValue <= 0) {
      alert('Discount must be greater than 0');
      return;
    }

    if (discountType === 'percent') {
      if (numericValue > 100) {
        alert('Percentage discount cannot exceed 100%');
        return;
      }
    } else {
      if (numericValue > subtotal) {
        alert(`Fixed discount cannot exceed $${subtotal.toFixed(2)}`);
        return;
      }
    }

    onApplyDiscount(discountType, numericValue);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Apply Discount</Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Select
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value as 'percent' | 'fixed')}
            fullWidth
          >
            <MenuItem value="percent">Percentage</MenuItem>
            <MenuItem value="fixed">Amount</MenuItem>
          </Select>
          
          <TextField
            fullWidth
            type="number"
            label={discountType === 'percent' ? 'Discount %' : 'Discount Amount'}
            value={isFocused ? inputValue : inputValue === '' ? '0' : inputValue}
            onChange={(e) => {
              const value = e.target.value;
              // Allow empty string or valid numbers
              if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                setInputValue(value);
              }
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            inputProps={{
              min: 0,
              max: discountType === 'percent' ? 100 : subtotal,
              step: discountType === 'percent' ? 1 : 0.01
            }}
            InputProps={{
              startAdornment: discountType === 'fixed' ? '$' : undefined,
              endAdornment: discountType === 'percent' ? '%' : undefined
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button fullWidth variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            fullWidth 
            variant="outlined" 
            onClick={() => {
              setInputValue('0');
              onApplyDiscount('percent', 0);
            }}
          >
            Clear Discount
          </Button>
          <Button fullWidth variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default DiscountModal;